import { DateCalendar } from "@/components/dates/DateCalendar";
import { IconCalendar } from "@/components/icons";
import { useAnchoredOverlay } from "@/hooks/useAnchoredOverlay";
import { useClickOutside } from "@/hooks/useClickOutside";
import {
  displayDateToIso,
  displayTimeToHm,
  formatIsoDate,
  isoDatePart,
  isoToDisplayDate,
  isoToDisplayTime,
  maskDisplayDate,
  maskDisplayTime,
} from "@/lib/dates/isoDate";
import { normalizeDateTimeSubmit } from "@/lib/forms/dateField";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getInputClassName } from "../../field-ui";

interface DatePickerControlProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  withTime?: boolean;
  invalid?: boolean;
  min?: string;
  max?: string;
  calendarLabel?: string;
  calendarOpen?: boolean;
  calendarId?: string;
  onCalendarToggle?: () => void;
  onDateFocus?: () => void;
  timeLabel?: string;
  onDateBlur?: (draft: string) => void;
  onTimeBlur?: (draft: string) => void;
  ariaLabel?: string;
}

function submitValue(isoDate: string, timeDisplay: string, withTime: boolean) {
  if (!isoDate) return "";
  if (!withTime) return isoDate;
  const time = displayTimeToHm(timeDisplay) ? timeDisplay : "00:00";
  return normalizeDateTimeSubmit(`${isoDate}T${time}`);
}

export function DatePickerControl({
  id,
  value,
  onChange,
  withTime = false,
  invalid = false,
  min,
  max,
  calendarLabel = "Открыть календарь",
  calendarOpen,
  calendarId,
  onCalendarToggle,
  onDateFocus,
  timeLabel = "Время",
  onDateBlur,
  onTimeBlur,
  ariaLabel,
}: DatePickerControlProps) {
  const pickerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dateDraft, setDateDraft] = useState(() => isoToDisplayDate(value));
  const [timeDraft, setTimeDraft] = useState(() =>
    withTime && value ? isoToDisplayTime(value) : "",
  );
  const [open, setOpen] = useState(false);
  const isOpen = onCalendarToggle ? Boolean(calendarOpen) : open;
  const controlsId = calendarId ?? `${id}-calendar`;

  useEffect(() => {
    setDateDraft(isoToDisplayDate(value));
    setTimeDraft(withTime && value ? isoToDisplayTime(value) : "");
  }, [value, withTime]);

  const applyDate = (iso: string, time = timeDraft, close = true) => {
    const next = submitValue(iso, time, withTime);
    setDateDraft(isoToDisplayDate(next));
    setTimeDraft(withTime && next ? isoToDisplayTime(next) : "");
    onChange(next);
    if (close) setOpen(false);
  };

  const clear = () => {
    setDateDraft("");
    setTimeDraft("");
    onChange("");
    setOpen(false);
  };

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(pickerRef, close, open, overlayRef);
  useAnchoredOverlay({
    open,
    triggerRef: pickerRef,
    overlayRef,
    align: "start",
    matchTriggerWidth: false,
    minHeight: 280,
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <div ref={pickerRef} className="relative min-w-0">
      <div
        className={
          withTime
            ? "flex flex-col gap-2 sm:flex-row sm:items-stretch"
            : undefined
        }
      >
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            value={dateDraft}
            inputMode="numeric"
            autoComplete="off"
            placeholder="ДД.ММ.ГГГГ"
            aria-label={ariaLabel}
            aria-invalid={invalid}
            aria-expanded={isOpen}
            aria-controls={controlsId}
            onFocus={onDateFocus}
            onChange={(event) => {
              const next = maskDisplayDate(event.target.value);
              setDateDraft(next);
              const iso = displayDateToIso(next);
              if (iso) applyDate(iso, timeDraft, false);
              if (!next) onChange("");
            }}
            onBlur={(event) => {
              if (pickerRef.current?.contains(event.relatedTarget as Node))
                return;
              if (overlayRef.current?.contains(event.relatedTarget as Node))
                return;
              if (dateDraft && !displayDateToIso(dateDraft)) {
                setDateDraft(isoToDisplayDate(value));
                if (withTime)
                  setTimeDraft(value ? isoToDisplayTime(value) : "");
              }
              onDateBlur?.(dateDraft);
            }}
            className={`${getInputClassName(!invalid)} pr-10`}
          />
          <button
            type="button"
            title={calendarLabel}
            aria-label={calendarLabel}
            aria-expanded={isOpen}
            aria-controls={controlsId}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onCalendarToggle ?? (() => setOpen((current) => !current))}
            className="absolute inset-y-0 right-0.5 inline-flex w-9 items-center justify-center rounded-md text-slate-400 hover:text-slate-700"
          >
            <IconCalendar size={16} />
          </button>
        </div>
        {withTime ? (
          <input
            value={timeDraft}
            inputMode="numeric"
            autoComplete="off"
            placeholder="ЧЧ:ММ"
            aria-label={timeLabel}
            aria-invalid={invalid}
            onChange={(event) => {
              const next = maskDisplayTime(event.target.value);
              setTimeDraft(next);
              const iso = displayDateToIso(dateDraft) ?? isoDatePart(value);
              if (iso && displayTimeToHm(next)) applyDate(iso, next, false);
            }}
            onBlur={() => {
              if (timeDraft && !displayTimeToHm(timeDraft)) {
                setTimeDraft(value ? isoToDisplayTime(value) : "");
              }
              onTimeBlur?.(timeDraft);
            }}
            className={getInputClassName(!invalid).replace(
              "w-full",
              "w-full sm:w-[6.75rem] sm:shrink-0",
            )}
          />
        ) : null}
      </div>
      {open && !onCalendarToggle
        ? createPortal(
            <div
              ref={overlayRef}
              id={`${id}-calendar`}
              role="dialog"
              aria-label="Календарь"
              data-admin-overlay=""
              onMouseDown={(event) => event.preventDefault()}
              className="fixed z-50 w-[17.5rem] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
            >
              <DateCalendar
                value={value}
                min={min}
                max={max}
                onSelect={(iso) => applyDate(iso)}
                onToday={() => applyDate(formatIsoDate(new Date()))}
                onClear={clear}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
